import { vitrinePushApi } from '@/api/vitrinePush';

/**
 * In-app OTP rail B (Web Push) enable/disable, driven by the Settings toggle. Best-effort: if the browser
 * doesn't support push, the backend has no VAPID key, or the user denies permission, we just skip — rail A
 * (the in-session banner) keeps working. See docs/IN_APP_OTP_PUSH_DESIGN.md §3.6/§5.
 */

export function webPushSupported(): boolean {
  return typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && typeof window !== 'undefined'
    && 'PushManager' in window
    && 'Notification' in window;
}

/** VAPID public key (base64url) → Uint8Array for pushManager.subscribe. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Subscribe this device to Web Push. Returns true only if a subscription was registered. */
export async function enableWebPush(): Promise<boolean> {
  if (!webPushSupported()) return false;
  const publicKey = await vitrinePushApi.vapidPublicKey();
  if (!publicKey) return false;                       // backend Web Push not configured
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;         // denial is fine — rail A still delivers

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
  }
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false;
  await vitrinePushApi.subscribe({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
  return true;
}

/**
 * Ensure a Web Push subscription exists WITHOUT ever prompting — acts only when notification permission was
 * already granted. Fixes the "toggle shows ON but push was never subscribed, so you had to switch it off and
 * on again" mismatch: the rail-A pref defaults ON, but rail-B only subscribed on an explicit toggle. Called
 * on Settings load when the pref is ON, so an already-granted device silently (re)registers its subscription.
 */
export async function ensureWebPushIfGranted(): Promise<boolean> {
  if (!webPushSupported()) return false;
  if (Notification.permission !== 'granted') return false;   // no permission yet → needs a user gesture; skip
  return enableWebPush().catch(() => false);                 // requestPermission() is a no-op when granted
}

/** Unsubscribe this device (best-effort). */
export async function disableWebPush(): Promise<void> {
  if (!webPushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    await vitrinePushApi.unsubscribe(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  } catch {
    /* best-effort */
  }
}
